import {
    Alert,
    Anchor,
    Button,
    Card,
    Group,
    Modal,
    NumberInput,
    Pill,
    SegmentedControl,
    Stack,
    TagsInput,
    Text,
    TextInput,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useMemo, useState, type FC } from 'react';

import {
    createReportSchedule,
    describeScheduleFilters,
    removeScheduleFilter,
    updateReportSchedule,
    type ReportSchedule,
    type ReportScheduleBuiltinType,
    type ReportScheduleFilters,
    type ReportScheduleFrequency,
} from '@api/reportSchedules';
import useToaster from '@hooks/toaster/useToaster';

import ScheduleDaysPicker from './ScheduleDaysPicker';
import { ALL_DAYS, computeNextRunIst, daysForFrequency, formatNextRun } from './scheduleTiming';

type Props = {
    opened: boolean;
    onClose: () => void;
    defaultName: string;
    currentUserEmail: string;
    /** Set for BUILTIN report schedules (e.g. the Inventory snapshot). */
    builtinReportType?: ReportScheduleBuiltinType;
    /** Set to schedule a user alert's findings instead of a builtin report. */
    alertId?: string;
    existing?: ReportSchedule | null;
    currentFilters?: ReportScheduleFilters;
    onCreated?: (scheduleId: string) => void;
    onUpdated?: (schedule: ReportSchedule) => void;
};

const ScheduleReportModal: FC<Props> = ({
    opened,
    onClose,
    defaultName,
    builtinReportType,
    alertId,
    currentUserEmail,
    existing,
    currentFilters,
    onCreated,
    onUpdated,
}) => {
    const { showToastSuccess, showToastError } = useToaster();
    const isEditing = !!existing;
    const isAlert = existing
        ? existing.sourceKind === 'ALERT'
        : !!alertId;

    const [name, setName] = useState(existing?.name ?? defaultName);
    const [frequency, setFrequency] = useState<ReportScheduleFrequency>(
        existing?.frequency ?? 'DAILY',
    );
    const [triggerTime, setTriggerTime] = useState(existing?.triggerTime ?? '08:00');
    const [days, setDays] = useState<number[]>(existing?.triggerDaysOfWeek ?? ALL_DAYS);
    const [dayOfMonth, setDayOfMonth] = useState<number | string>(
        existing?.triggerDayOfMonth ?? 1,
    );
    const [recipients, setRecipients] = useState<string[]>(
        existing?.recipients ?? (currentUserEmail ? [currentUserEmail] : []),
    );
    const [filters, setFilters] = useState<ReportScheduleFilters>(
        existing?.filters ?? currentFilters ?? {},
    );
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const filterChips = useMemo(() => describeScheduleFilters(filters), [filters]);
    const hasFilters = filterChips.length > 0;

    const reportType = existing?.builtinReportType ?? builtinReportType;
    const clearFiltersLabel =
        reportType === 'MATERIAL_STOCK_BY_FACILITY'
            ? 'Send all materials instead'
            : 'Send all SKUs instead';

    const nextRun = useMemo(
        () =>
            computeNextRunIst(
                frequency,
                triggerTime,
                days,
                frequency === 'MONTHLY' ? Number(dayOfMonth) : undefined,
            ),
        [frequency, triggerTime, days, dayOfMonth],
    );

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSubmit = async () => {
        setError(null);
        setSubmitting(true);
        try {
            if (isEditing && existing) {
                const updated = await updateReportSchedule(existing.id, {
                    name: name.trim() || defaultName,
                    recipients,
                    frequency,
                    triggerTime,
                    triggerDaysOfWeek: frequency === 'MONTHLY' ? undefined : days,
                    triggerDayOfMonth:
                        frequency === 'MONTHLY' ? Number(dayOfMonth) : undefined,
                    filters: hasFilters ? filters : {},
                });
                showToastSuccess({
                    title: 'Schedule updated',
                    subtitle: nextRun
                        ? `Next email will arrive ${formatNextRun(nextRun)}`
                        : 'Changes saved.',
                });
                onUpdated?.(updated);
            } else {
                const created = await createReportSchedule({
                    name: name.trim() || defaultName,
                    sourceKind: isAlert ? 'ALERT' : 'BUILTIN',
                    builtinReportType: isAlert ? undefined : builtinReportType,
                    alertId: isAlert ? alertId : undefined,
                    recipients,
                    frequency,
                    triggerTime,
                    triggerDaysOfWeek: frequency === 'MONTHLY' ? undefined : days,
                    triggerDayOfMonth:
                        frequency === 'MONTHLY' ? Number(dayOfMonth) : undefined,
                    enabled: true,
                    filters: isAlert || !hasFilters ? undefined : filters,
                });
                showToastSuccess({
                    title: 'Schedule created',
                    subtitle: nextRun
                        ? `First email will arrive ${formatNextRun(nextRun)}`
                        : `Your report has been scheduled.`,
                });
                onCreated?.(created.id);
            }
            handleClose();
        } catch (e: any) {
            const message =
                e?.error?.message ||
                e?.message ||
                (isEditing
                    ? 'Failed to update the schedule. Please try again.'
                    : 'Failed to create the schedule. Please try again.');
            setError(String(message));
            showToastError({
                title: isEditing ? 'Could not update schedule' : 'Could not create schedule',
                subtitle: String(message),
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={
                isEditing
                    ? 'Edit scheduled email'
                    : isAlert
                      ? 'Email me this alert'
                      : 'Email me this report'
            }
            size="md"
        >
            <Stack gap="md">
                <TextInput
                    label="Schedule name"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    maxLength={120}
                />

                <div>
                    <Text size="sm" fw={500} mb={6}>
                        Frequency
                    </Text>
                    <SegmentedControl
                        fullWidth
                        color="indigo"
                        value={frequency}
                        onChange={(v) => {
                            const next = v as ReportScheduleFrequency;
                            setFrequency(next);
                            setDays((current) => daysForFrequency(next, current));
                        }}
                        data={[
                            { value: 'DAILY', label: 'Daily' },
                            { value: 'WEEKLY', label: 'Weekly' },
                            { value: 'MONTHLY', label: 'Monthly' },
                        ]}
                    />
                </div>

                {frequency !== 'MONTHLY' && (
                    <ScheduleDaysPicker frequency={frequency} days={days} onChange={setDays} />
                )}

                {frequency === 'MONTHLY' && (
                    <NumberInput
                        label="Day of the month"
                        description="1-28 (we skip 29-31 to keep monthly cadence consistent)"
                        min={1}
                        max={28}
                        value={dayOfMonth}
                        onChange={setDayOfMonth}
                    />
                )}

                <TimeInput
                    label="Time"
                    description="Reparo uses India Standard Time (IST)"
                    // Uncontrolled: a controlled type="time" input reverts the
                    // browser's in-progress segment edit on each re-render.
                    defaultValue={triggerTime}
                    onChange={(e) => setTriggerTime(e.currentTarget.value)}
                />

                <TagsInput
                    label="Send to"
                    description="Any email address — press Enter after each"
                    placeholder="Add an email and press Enter"
                    value={recipients}
                    onChange={setRecipients}
                />

                {isAlert && (
                    <Card withBorder p="sm" bg="indigo.0" radius="sm">
                        <Text size="xs" c="indigo.9">
                            Each email contains the alert's current findings as a CSV
                            attachment, captured at send time. The alert keeps
                            evaluating on its own schedule — this only controls
                            delivery.
                        </Text>
                    </Card>
                )}

                {!isAlert && hasFilters && (
                    <Card withBorder p="sm" bg="indigo.0" radius="sm">
                        <Stack gap={6}>
                            <Text size="xs" fw={500} c="indigo.9">
                                This schedule will be generated with:
                            </Text>
                            <Group gap={6}>
                                {filterChips.map((chip) => (
                                    <Pill
                                        key={chip.key}
                                        // A fixed chip describes HOW the numbers are computed
                                        // (rate basis, rate window, hidden columns) rather than
                                        // which rows are in scope. Removing it would produce a
                                        // different report, not a wider one, so it has no
                                        // remove button.
                                        withRemoveButton={!chip.fixed}
                                        onRemove={
                                            chip.fixed
                                                ? undefined
                                                : () =>
                                                      setFilters((prev) =>
                                                          removeScheduleFilter(prev, chip),
                                                      )
                                        }
                                    >
                                        {chip.label}
                                    </Pill>
                                ))}
                            </Group>
                            <Anchor
                                size="xs"
                                c="indigo.7"
                                onClick={() => setFilters({})}
                            >
                                {clearFiltersLabel}
                            </Anchor>
                        </Stack>
                    </Card>
                )}

                {nextRun && (
                    <Text size="sm" c="indigo.7">
                        Next run: {formatNextRun(nextRun)}
                    </Text>
                )}

                {error && (
                    <Alert color="red" variant="light">
                        {error}
                    </Alert>
                )}

                <Group justify="flex-end" mt="sm">
                    <Button variant="default" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        color="indigo"
                        loading={submitting}
                        onClick={handleSubmit}
                        disabled={recipients.length === 0 || !triggerTime || (frequency !== 'MONTHLY' && days.length === 0)}
                    >
                        {isEditing ? 'Save changes' : 'Schedule'}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ScheduleReportModal;
